import {
  Button,
  Checkbox,
  IconButton,
  Input,
  MenuItem,
  Select,
  Stack,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import {
  Controller,
  useFieldArray,
  UseFieldArrayReturn,
  useFormContext,
} from 'react-hook-form';
import {
  DynamicIcon,
  EMAIL_VALIDATION_REGEX,
  MAX_NUMBER_LENGTH,
  MIN_NUMBER_LENGTH,
  Tooltip,
  WithTooltip,
} from 'src/components';
import { TextMaskPhone } from 'src/components/Forms/FormPhoneInput/form-phone-input';
import { ClientFormDynamicFieldsName } from './client-fields';

export interface DynamicPropertyItem {
  id: string;
  value: string;
  valueId: string;
  labelId: number;
  isPrimary: boolean;
}

export interface DynamicProperty {
  labels?: {
    id: number;
    label: string;
  }[];
  title: string;
  type: string;
  name: ClientFormDynamicFieldsName;
  placeholder?: string;
  icon: string;
  iconFilled?: boolean;
}

export const StyledLabel = styled(Stack, { name: 'AppWrapper' })(({ theme }) => ({
  padding: theme.spacing(0, 1.5),
  border: '1px solid',
  borderColor: theme.palette.custom.OV.sys.outlineVariant,
  borderRadius: theme.spacing(1),

  '&:hover': {
    borderColor: theme.palette.text.primary,
  },

  '&:focus, &:has(.Mui-focused)': {
    outline: '1px solid',
    outlineColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
  },

  '& .OVIconButton-delete': {
    visibility: 'hidden',
    opacity: 0,
    transitionProperty: 'opacity, visibility',
    transitionDuration: '0.25s',
  },

  '&:hover .OVIconButton-delete': {
    visibility: 'visible',
    opacity: 1,
  },

  '&:has(.Mui-error)': {
    borderColor: theme.palette.error.main,
    outlineColor: theme.palette.error.main,
  },
}));

export const DynamicItem = ({
  property,
  index,
  fieldArrayMethods,
}: {
  property: DynamicProperty;
  index: number;
  fieldArrayMethods: UseFieldArrayReturn<
    Record<ClientFormDynamicFieldsName, DynamicPropertyItem[]>,
    ClientFormDynamicFieldsName
  >;
}) => {
  const { control, formState, setValue } =
    useFormContext<Record<ClientFormDynamicFieldsName, DynamicPropertyItem[]>>();

  const { fields, remove, update } = fieldArrayMethods;

  const errorMsg = formState.errors[property.name]?.[index]?.value?.message;
  const hasErrorMsg = typeof errorMsg === 'string';

  return (
    <StyledLabel direction="row" alignItems="center" gap={1}>
      {property.type === 'tel' ? (
        <Controller
          name={`${property.name}.${index}.value`}
          rules={{
            required: true,
            minLength: MIN_NUMBER_LENGTH,
            maxLength: MAX_NUMBER_LENGTH,
          }}
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              error={hasErrorMsg}
              placeholder={property.placeholder ?? property.title}
              type={property.type}
              inputComponent={TextMaskPhone}
              disableUnderline
              required
              sx={{
                flex: 1,
              }}
              inputProps={{
                sx: {
                  fontSize: '0.875rem',
                  paddingBlock: 1,
                },
              }}
            />
          )}
        />
      ) : (
        <Controller
          name={`${property.name}.${index}.value`}
          control={control}
          rules={{
            required: true,
            pattern: property.type === 'email' ? EMAIL_VALIDATION_REGEX : undefined,
          }}
          render={({ field: props }) => (
            <TextField
              {...props}
              error={!!hasErrorMsg}
              type={property.type}
              placeholder={property.placeholder ?? property.title}
              variant="standard"
              size="small"
              required
              inputProps={{
                sx: {
                  fontSize: '0.875rem',
                  paddingBlock: 1,
                },
              }}
              sx={{
                flex: 1,
              }}
              InputProps={{
                disableUnderline: true,
              }}
            />
          )}
        />
      )}
      <Controller
        name={`${property.name}.${index}.labelId`}
        control={control}
        render={({ field }) => {
          return (
            <Select
              inputProps={{
                sx: {
                  padding: 0,
                },
              }}
              {...field}
              displayEmpty
              renderValue={(selected) => {
                if (typeof selected !== 'number') {
                  return 'Label';
                }

                return (
                  property.labels?.find((label) => label.id === selected)?.label ?? 'Label'
                );
              }}
              disableUnderline
              variant="standard"
            >
              {property.labels?.map((item) => (
                <MenuItem key={property.title + item.id.toString()} value={item.id}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          );
        }}
      />
      <Controller
        name={`${property.name}.${index}.isPrimary`}
        control={control}
        render={({ field }) => {
          return (
            <WithTooltip
              title={`Set as primary ${property.title}`}
              hideTooltip={!!field.value}
            >
              <Checkbox
                {...field}
                onChange={(e) => {
                  fields.forEach((_, i) => {
                    setValue(
                      `${property.name}.${i}.isPrimary`,
                      // There is an issue with types in react-hook-form:
                      // setValue()'s first parameter is always of type 'never'
                      false as never,
                    );
                  });
                  field?.onChange(e.target.checked);
                }}
                disabled={!!field.value}
                checked={!!field.value}
                icon={<DynamicIcon name="star_outline" fontSize="1.5rem" />}
                checkedIcon={
                  <DynamicIcon name="star_outline" filled color="#F6A609" fontSize="1.5rem" />
                }
              />
            </WithTooltip>
          );
        }}
      />
      <Tooltip title={`Delete ${property.title}`}>
        <IconButton
          onClick={() => {
            // Calling remove won't remove the Element immediately from fields.
            const tempFields = structuredClone(fields);
            const removedElement = tempFields.splice(index, 1)[0];
            remove(index);
            if (removedElement?.isPrimary && tempFields.length > 0) {
              update(0, {
                ...tempFields[0],
                isPrimary: true,
              });
            }
          }}
          className="OVIconButton-delete"
          sx={{
            alignSelf: 'start',
          }}
        >
          <DynamicIcon name="cancel" filled />
        </IconButton>
      </Tooltip>
    </StyledLabel>
  );
};

export const DynamicPropertyElement = ({ property }: { property: DynamicProperty }) => {
  const { control } =
    useFormContext<Record<ClientFormDynamicFieldsName, DynamicPropertyItem[]>>();

  const fieldArrayMethods = useFieldArray({
    name: property.name,
    control,
  });

  return (
    <Stack gap={2}>
      <Stack direction="row" gap={1} alignItems="center">
        <DynamicIcon name={property.icon} filled={property.iconFilled} fontSize="1.4rem" />
        <Typography variant="body2">{property.title}</Typography>
      </Stack>
      {fieldArrayMethods.fields.map((field, index) => (
        <DynamicItem
          key={property.title + field.id}
          property={property}
          index={index}
          fieldArrayMethods={fieldArrayMethods}
        />
      ))}
      <Button
        onClick={() => {
          const hasPrimary = fieldArrayMethods.fields.some((field) => field.isPrimary);

          fieldArrayMethods.append({
            id: '',
            isPrimary: !hasPrimary,
            value: '',
            valueId: '',
            labelId: 0,
          });
        }}
        startIcon={<DynamicIcon name="add_circle" filled />}
        sx={{
          alignSelf: 'start',
        }}
      >
        Add {property.title}
      </Button>
    </Stack>
  );
};
